import * as THREE from "three";
import TWEEN from "@tweenjs/tween.js";
import { ScriptingRuntime } from "../../nr-scripting-runtime/core/ScriptingRuntime";
import { proxyUrlShortenerMS } from "../utils/media-url-utils";
import { Persistable } from "../../nr-scripting-runtime/api/Persistable";
import configs from "../utils/configs";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { MediaFrame, Networked } from "../bit-components";
import { MediaType } from "../utils/media-utils";
import { mediaFrameEcsInit } from "../utils/media-frame-ecs-init";
import { snappeableEcsInit } from "../utils/snappeable-ecs-init";
import { mediaInflatorNoResize } from "../gltf-component-mappings.js";
import { Input } from "../../nr-scripting-runtime/api/Input";
import { destroy as createDestroyHelper } from "../../nr-scripting-runtime/api/Destroy";
import {
  patchedSceneAddFactory,
  patchedSceneGetObjectByNameFactory,
  patchedSceneGetObjectByPropertyFactory,
  patchedSceneGetObjectByIdFactory,
  patchedSceneGetObjectByUUIDFactory,
  patchObject3DFinders,
  patchedSceneRemoveFactory,
  patchedSceneTraverseFactory,
  patchedSceneTraverseVisibleFactory,
  patchedSceneTraverseAncestorsFactory,
  patchObject3DTraversals,
  patchedSceneAttachFactory,
  patchObject3DAddAttach,
  ScriptableObjects,
  ScriptableObject
} from "../../nr-scripting-runtime/core/scriptable/ScriptableObjects";

AFRAME.registerSystem("scripting", {
  init: function () {
    if (new URLSearchParams(location.search).has("debugScriptingRace")) {
      const scene = this.el.sceneEl;
      const snapshot = event => console.log("[scripting-race] " + JSON.stringify({
        event, hub: window.APP?.hub?.hub_id, ms: Math.round(performance.now()),
        rootSrc: document.querySelector("#environment-scene > [gltf-model-plus]")?.components?.["gltf-model-plus"]?.data?.src,
        names: Array.from(document.querySelectorAll("[behaviour]")).map(el => el.object3D?.name),
        registeredNames: (this.scriptingRuntime?._lifecycleHandlers || []).map(handler => handler.node?.name),
        behaviours: document.querySelectorAll("[behaviour]").length,
        registered: this.scriptingRuntime?._lifecycleHandlers?.length || 0,
        startTick: this.startTick, environment: this.environmentSceneLoaded, assets: this.allObjectsLoaded
      }));
      ["model-loading", "model-loaded", "all-objects-loaded", "environment-scene-loaded", "hub-change-started", "reset_scene"].forEach(name =>
        scene.addEventListener(name, e => snapshot(name + ":" + (e.target.id || e.target.className))));
      const raceParams = new URLSearchParams(location.search);
      const destination = raceParams.get("racePortal");
      if (destination) {
        const sourceHub = raceParams.get("hub_id");
        const button = document.createElement("button");
        button.id = "scripting-race-portal";
        button.textContent = "Test portal to " + destination;
        button.style.cssText = "position:fixed;top:8px;left:8px;z-index:2147483647;padding:12px;background:white;color:black";
        button.onclick = async () => {
          const nextHub = window.APP?.hub?.hub_id === destination ? sourceHub : destination;
          button.disabled = true;
          try {
            snapshot("PORTAL_REQUEST:" + nextHub);
            await window.changeHub(nextHub, true, null, raceParams.get("raceSmooth") === "1");
            snapshot("PORTAL_RETURN:" + nextHub);
            button.textContent = "Test portal to " + (nextHub === destination ? sourceHub : destination);
          } catch (error) {
            console.error("[scripting-race] PORTAL_ERROR", error);
          } finally {
            button.disabled = false;
          }
        };
        document.body.appendChild(button);
      }
      const initialize = this.initializeScriptingRuntime;
      this.initializeScriptingRuntime = async (...args) => {
        snapshot("INITIALIZE_BEGIN");
        await initialize.apply(this, args);
        snapshot("INITIALIZE_END");
      };
    }
    this.startTick = false;
    this.environmentSceneLoaded = false;
    this.allObjectsLoaded = false;

    this.onEnvironmentSceneLoaded = async () => {
      this.environmentSceneLoaded = true;
      await this.checkAndInitialize();
    };

    this.onAllObjectsLoaded = async () => {
      this.allObjectsLoaded = true;
      await this.checkAndInitialize();
    };

    this.onHubChangeStarted = () => {
      if (this.startTick) {
        console.log("scripting runtime paused awaiting new room load");
      }
      this.startTick = false;
      this.environmentSceneLoaded = false;
      this.allObjectsLoaded = false;

      if (this.destroyAllBehaviourNodes) {
        try {
          this.destroyAllBehaviourNodes();
        } catch (error) {
          console.warn("Failed to destroy behaviour nodes:", error);
        }
      }

      if (this.scriptingRuntime && typeof this.scriptingRuntime.reset === "function") {
        try {
          this.scriptingRuntime.reset();
        } catch (error) {
          console.warn("Failed to reset scripting runtime:", error);
        }
      }
    };

    if (typeof window.module === "undefined") {
      window.module = { exports: {} };
    }
    window.globalThis.THREE = THREE;
    window.globalThis.THREE.GLTFLoader = GLTFLoader;
    this.scriptingRuntime = new ScriptingRuntime({ devMode: false, proxyUrl: proxyUrlShortenerMS });
    this.destroyScriptObject = createDestroyHelper(this.scriptingRuntime);

    this.destroyAllBehaviourNodes = () => {
      const behaviourEls = document.querySelectorAll("[behaviour]");
      behaviourEls.forEach(el => {
        const object3D = el.object3D;
        if (!object3D) {
          return;
        }
        try {
          this.destroyScriptObject(object3D);
        } catch (error) {
          console.warn("Failed to destroy script object:", error);
        }
      });

      if (typeof this.scriptingRuntime.unregisterAllBehaviourNodes === "function") {
        this.scriptingRuntime.unregisterAllBehaviourNodes();
      } else {
        this.scriptingRuntime.reset();
      }
    };

    // Set runtime mode immediately when the system initializes
    ScriptableObject.setRuntimeMode("aframe");

    this.el.sceneEl.addEventListener("environment-scene-loaded", this.onEnvironmentSceneLoaded);

    this.el.sceneEl.addEventListener("all-objects-loaded", this.onAllObjectsLoaded);
    this.el.sceneEl.addEventListener("hub-change-started", this.onHubChangeStarted);

    this.behaviors = [];

    this.initializeScriptableObjects.bind(this);
  },
  remove: function () {
    this.el.sceneEl.removeEventListener("environment-scene-loaded", this.onEnvironmentSceneLoaded);
    this.el.sceneEl.removeEventListener("all-objects-loaded", this.onAllObjectsLoaded);
    this.el.sceneEl.removeEventListener("hub-change-started", this.onHubChangeStarted);
  },
  checkAndInitialize: async function () {
    if (this.environmentSceneLoaded && this.allObjectsLoaded && !this.startTick) {
      await this.initializeScriptingRuntime();
      this.startTick = true;
    }
  },
  initializeScriptingRuntime: async function () {
    if (configs.PERSISTENCE_API_URL && configs.PERSISTENCE_API_KEY) {
      Persistable.setup(this.el.sceneEl.object3D, "prod", configs.PERSISTENCE_API_URL, configs.PERSISTENCE_API_KEY);
    }

    Input.setup(AFRAME.scenes[0].renderer);

    this.initializeScriptableObjects();

    const behaviours = Array.from(document.querySelectorAll("[behaviour]"));

    behaviours.forEach(el => {
      el.object3D.scriptableType = "behavior";
      ScriptableObjects.makeScriptable(el.object3D);
    });

    behaviours.sort((a, b) => {
      const indexA = a.getAttribute("behaviour").executionIndex;
      const indexB = b.getAttribute("behaviour").executionIndex;
      return indexA - indexB;
    });
    for (const el of behaviours) {
      const behaviourComponent = el.components.behaviour;
      await behaviourComponent.register();
    }

    const aiAgents = Array.from(document.querySelectorAll("[ai-agent]"));

    for (const aiAgent of aiAgents) {
      const aiAgentComponent = aiAgent.components["ai-agent"];
      aiAgentComponent.registerPersistable();
    }
  },
  initializeScriptableObjects: function () {
    const scene = this.el.sceneEl.object3D;

    ScriptableObject.setMediaInflator(mediaInflatorNoResize);
    ScriptableObject.setMediaFrameEcsAccess({ MediaFrame, Networked, MediaType });
    ScriptableObject.setMediaFrameEcsInit(mediaFrameEcsInit);
    ScriptableObject.setSnapColliderEcsInit(snappeableEcsInit);

    const rootEl = document.querySelector("[environment-settings], [runtime-settings]") || this.el.sceneEl;
    Object.defineProperty(scene, "_aframeRootEl", {
      value: rootEl,
      writable: false,
      configurable: true
    });

    // Enable one-world DOM parenting for scriptables
    scene.add = patchedSceneAddFactory(scene, { sceneEl: rootEl });
    scene.attach = patchedSceneAttachFactory(scene, { sceneEl: rootEl });
    scene.remove = patchedSceneRemoveFactory(scene, { sceneEl: rootEl });
    scene.getObjectByName = patchedSceneGetObjectByNameFactory(scene);
    scene.getObjectByProperty =
      patchedSceneGetObjectByPropertyFactory(scene) || ScriptableObject.prototype.getObjectByProperty.bind(scene);
    scene.getObjectById =
      patchedSceneGetObjectByIdFactory(scene) || ScriptableObject.prototype.getObjectById.bind(scene);
    scene.getObjectByUUID =
      patchedSceneGetObjectByUUIDFactory(scene) || (uuid => scene.getObjectByProperty("uuid", uuid));

    scene.traverse = patchedSceneTraverseFactory(scene, { sceneEl: rootEl }) || scene.traverse;
    scene.traverseVisible = patchedSceneTraverseVisibleFactory(scene, { sceneEl: rootEl }) || scene.traverseVisible;
    scene.traverseAncestors =
      patchedSceneTraverseAncestorsFactory(scene, { sceneEl: rootEl }) || scene.traverseAncestors;

    // Ensure Object3D instances expose unified finders and traversal behavior
    patchObject3DFinders();
    patchObject3DTraversals();
    patchObject3DAddAttach(rootEl);
  },
  tick: function (time, dt) {
    // if (this.startTick === false) return;
    try {
      const newTime = time / 1000;
      const newDt = dt / 1000;
      this.scriptingRuntime.tick(newDt, newTime);
      TWEEN.update(time);
    } catch (error) {
      // no-op
    }
  }
});

AFRAME.registerComponent("behaviour", {
  schema: {
    code: { default: "" },
    executionIndex: { type: "number", default: 0 },
    slotRegistry: { type: "array", default: [] }
  },
  async register() {
    this.scriptingRuntime = AFRAME.scenes[0].systems.scripting.scriptingRuntime;
    this.el.object3D.code = this.data.code;
    this.el.object3D.enabled = true;

    // First, store the original clone method
    const originalClone = this.el.object3D.clone.bind(this.el.object3D);

    // Override the clone method
    this.el.object3D.clone = () => {
      // Call the original clone method
      const cl = originalClone();

      // Assign the 'code' property to the cloned object
      cl.code = this.data.code;
      cl.executionIndex = this.data.executionIndex;
      return cl;
    };

    const camera = document.querySelector("#viewing-camera")?.object3D.children[0];
    const avatarRig = document.getElementById("avatar-rig")?.object3D;
    const avatarPOV = document.getElementById("avatar-pov-node")?.object3D;

    await this.scriptingRuntime.registerBehaviourNode(this.el.object3D, {
      scene: AFRAME.scenes[0].object3D,
      THREE,
      TWEEN,
      animate: target => new TWEEN.Tween(target),
      camera,
      avatarRig,
      avatarPOV,
      renderer: AFRAME.scenes[0].renderer,
      audioListener: APP.audioListener
    });
  },
  remove() {
    this.scriptingRuntime.unregisterBehaviourNode(this.el.object3D);
  }
});
